import type { CanvasSize, WorldBounds } from '../../../../lib/simulation-core'
import type { BraitenbergWorld, VehicleConfig, VehicleState } from '../engine/types'

const BACKGROUND = '#e7ded2'
const SOURCE_COLOR = 'rgba(128, 97, 47, 0.48)'

function toCanvas(
  wx: number,
  wy: number,
  bounds: WorldBounds,
  size: CanvasSize,
): [number, number] {
  return [(wx / bounds.width) * size.width, (wy / bounds.height) * size.height]
}

function scaleToCanvas(worldUnits: number, bounds: WorldBounds, size: CanvasSize): number {
  return (worldUnits / bounds.width) * size.width
}

function drawSource(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  bounds: WorldBounds,
  size: CanvasSize,
): void {
  const [cx, cy] = toCanvas(x, y, bounds, size)
  const r = scaleToCanvas(8, bounds, size)
  const outerR = scaleToCanvas(25, bounds, size)

  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerR)
  const color = SOURCE_COLOR
  gradient.addColorStop(0, color)
  gradient.addColorStop(0.4, color)
  gradient.addColorStop(1, 'transparent')

  ctx.beginPath()
  ctx.arc(cx, cy, outerR, 0, Math.PI * 2)
  ctx.fillStyle = gradient
  ctx.fill()

  // Small solid core
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(128, 97, 47, 0.34)'
  ctx.fill()
}

function drawTrail(
  ctx: CanvasRenderingContext2D,
  trail: readonly { x: number; y: number }[],
  color: string,
  bounds: WorldBounds,
  size: CanvasSize,
): void {
  if (trail.length < 2) return

  const lineWidth = Math.max(1, scaleToCanvas(0.5, bounds, size))
  ctx.lineWidth = lineWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  for (let i = 1; i < trail.length; i++) {
    const alpha = (i / trail.length) * 0.4
    ctx.strokeStyle = color
    ctx.globalAlpha = alpha

    const [x0, y0] = toCanvas(trail[i - 1].x, trail[i - 1].y, bounds, size)
    const [x1, y1] = toCanvas(trail[i].x, trail[i].y, bounds, size)

    // Skip if segment wraps around (toroidal jump)
    const dx = Math.abs(x1 - x0)
    const dy = Math.abs(y1 - y0)
    if (dx > size.width * 0.5 || dy > size.height * 0.5) continue

    ctx.beginPath()
    ctx.moveTo(x0, y0)
    ctx.lineTo(x1, y1)
    ctx.stroke()
  }
  ctx.globalAlpha = 1
}

function drawVehicle(
  ctx: CanvasRenderingContext2D,
  state: VehicleState,
  config: VehicleConfig,
  bounds: WorldBounds,
  size: CanvasSize,
): void {
  const [cx, cy] = toCanvas(state.position.x, state.position.y, bounds, size)
  const length = scaleToCanvas(6, bounds, size)
  const halfWidth = scaleToCanvas(4, bounds, size)

  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(state.heading)

  // Triangle body
  ctx.beginPath()
  ctx.moveTo(length, 0)
  ctx.lineTo(-length * 0.6, -halfWidth)
  ctx.lineTo(-length * 0.6, halfWidth)
  ctx.closePath()
  ctx.globalAlpha = 0.85
  ctx.fillStyle = config.color
  ctx.fill()
  ctx.globalAlpha = 1

  // Sensor dots
  const sensorR = scaleToCanvas(1, bounds, size)
  for (const sensor of config.sensors) {
    const sx = scaleToCanvas(sensor.offset.x, bounds, size)
    const sy = scaleToCanvas(sensor.offset.y, bounds, size)
    ctx.beginPath()
    ctx.arc(sx, sy, sensorR, 0, Math.PI * 2)
    ctx.fillStyle = config.color
    ctx.globalAlpha = 0.5
    ctx.fill()
    ctx.globalAlpha = 1
  }

  ctx.restore()
}

export function renderWorld(
  ctx: CanvasRenderingContext2D,
  world: BraitenbergWorld,
  configs: VehicleConfig[],
  canvasSize: CanvasSize,
): void {
  const { vehicles, sources, bounds } = world

  // Clear
  ctx.fillStyle = BACKGROUND
  ctx.fillRect(0, 0, canvasSize.width, canvasSize.height)

  // Sources
  for (const source of sources) {
    drawSource(ctx, source.position.x, source.position.y, bounds, canvasSize)
  }

  // Trails
  for (let i = 0; i < vehicles.length; i++) {
    drawTrail(ctx, vehicles[i].trail, configs[i].color, bounds, canvasSize)
  }

  // Vehicles
  for (let i = 0; i < vehicles.length; i++) {
    drawVehicle(ctx, vehicles[i], configs[i], bounds, canvasSize)
  }
}

export function hitTestSource(
  canvasX: number,
  canvasY: number,
  world: BraitenbergWorld,
  canvasSize: CanvasSize,
): number {
  const threshold = scaleToCanvas(8, world.bounds, canvasSize)
  const thresholdSq = threshold * threshold

  for (let i = 0; i < world.sources.length; i++) {
    const [sx, sy] = toCanvas(
      world.sources[i].position.x,
      world.sources[i].position.y,
      world.bounds,
      canvasSize,
    )
    const dx = canvasX - sx
    const dy = canvasY - sy
    if (dx * dx + dy * dy < thresholdSq) return i
  }
  return -1
}

export function canvasToWorld(
  canvasX: number,
  canvasY: number,
  bounds: WorldBounds,
  canvasSize: CanvasSize,
): { x: number; y: number } {
  return {
    x: (canvasX / canvasSize.width) * bounds.width,
    y: (canvasY / canvasSize.height) * bounds.height,
  }
}
